import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useBrand } from "../../context/BrandContext";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import { canExchangeSeatedPlayers } from "../../utils/coachSessionTables";
import { CoachTablePreviewCard } from "./CoachTablePreviewCard";
import "./CoachTablePreviewCard.css";
import "./LiveTrainingTableSetupWizard.css";
import "./LiveTrainingTableSwapDialog.css";

const TABLES_PER_PAGE_MOBILE = 5;

/** Match CoachTablesDialog: 5×1 mobile, 6×2 tablet, 9×3 desktop. */
function resolveTablesLayout() {
  if (typeof window === "undefined") {
    return { tablesPerPage: TABLES_PER_PAGE_MOBILE, columns: 1 };
  }
  const w = Math.round(
    window.visualViewport?.width || window.innerWidth || 390
  );
  if (w >= 1100) return { tablesPerPage: 9, columns: 3 };
  if (w >= 720) return { tablesPerPage: 6, columns: 2 };
  return { tablesPerPage: TABLES_PER_PAGE_MOBILE, columns: 1 };
}

function slotKey(tableNumber, slot) {
  return `${tableNumber}:${slot}`;
}

function seatIdentity(table, slot) {
  return `${table?.tableId ?? table?.tableNumber}:${slot}`;
}

function tableOrderKey(table) {
  return String(table?.tableId ?? table?.tableNumber ?? "");
}

function padPageSlots(pageItems, pageSize) {
  return Array.from({ length: pageSize }, (_, i) => pageItems[i] ?? null);
}

function chunkTables(list, size) {
  const pages = [];
  for (let i = 0; i < list.length; i += size) {
    pages.push(list.slice(i, i + size));
  }
  return pages.length ? pages : [[]];
}

export function LiveTrainingTableSwapDialog({
  open,
  onClose,
  tables = [],
  tableSetup,
  exerciseLocked = false,
  roomCapacity = 0,
  usedTableCount = 0,
  stations = null,
  focusedTsgId = null,
  stationLabelsById = null,
  teId = null,
  /** Stack above session/exercise results overlays. */
  elevated = false,
}) {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const brandPrimary = brand?.color_primary || "#0877BC";
  const brandSecondary = brand?.color_secondary || "#46528b";
  const [mode, setMode] = useState("swap"); // 'swap' | 'move'
  const [swapSource, setSwapSource] = useState(null);
  const [moveSource, setMoveSource] = useState(null);
  const [flashSlots, setFlashSlots] = useState(() => new Set());
  const [orderKeys, setOrderKeys] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [moveBusy, setMoveBusy] = useState(false);
  const [boardTsgId, setBoardTsgId] = useState(null);
  const [foreignTables, setForeignTables] = useState([]);
  const [foreignLoading, setForeignLoading] = useState(false);
  const [tablesPerPage, setTablesPerPage] = useState(TABLES_PER_PAGE_MOBILE);
  const [gridColumns, setGridColumns] = useState(1);
  const pagesRef = useRef(null);
  const flashTimerRef = useRef(null);

  const multiStation = Array.isArray(stations) && stations.length > 1;
  const viewingOther =
    multiStation &&
    boardTsgId != null &&
    focusedTsgId != null &&
    String(boardTsgId) !== String(focusedTsgId);
  const displayTables = viewingOther
    ? foreignTables
    : tableSetup?.tables ?? tables;

  useEffect(() => {
    if (!open) {
      setBoardTsgId(focusedTsgId != null ? String(focusedTsgId) : null);
      setForeignTables([]);
      setForeignLoading(false);
      return;
    }
    setBoardTsgId(focusedTsgId != null ? String(focusedTsgId) : null);
  }, [open, focusedTsgId]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !viewingOther || !boardTsgId) {
      setForeignTables([]);
      setForeignLoading(false);
      return undefined;
    }
    setForeignTables([]);
    setForeignLoading(true);
    void (async () => {
      const rows =
        (await tableSetup?.loadStationTables?.(boardTsgId)) || [];
      if (cancelled) return;
      setForeignTables(rows);
      setForeignLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, viewingOther, boardTsgId, tableSetup?.loadStationTables]);

  useEffect(() => {
    if (!open) return undefined;
    const apply = () => {
      const { tablesPerPage: nextCount, columns } = resolveTablesLayout();
      setTablesPerPage(nextCount);
      setGridColumns(columns);
    };
    apply();
    window.addEventListener("resize", apply);
    window.visualViewport?.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      window.visualViewport?.removeEventListener("resize", apply);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setMode("swap");
      setSwapSource(null);
      setMoveSource(null);
      setFlashSlots(new Set());
      setOrderKeys([]);
      setPageIndex(0);
      setMoveBusy(false);
      return;
    }
    setMode("swap");
    setSwapSource(null);
    setMoveSource(null);
    setPageIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const sorted = [...(displayTables || [])].sort(
      (a, b) => (a.tableNumber || 0) - (b.tableNumber || 0)
    );
    const keys = sorted.map(tableOrderKey).filter(Boolean);
    setOrderKeys((prev) => {
      if (prev.length === 0) return keys;
      const keySet = new Set(keys);
      const next = prev.filter((k) => keySet.has(k));
      keys.forEach((k) => {
        if (!next.includes(k)) next.push(k);
      });
      return next;
    });
  }, [open, displayTables]);

  const setupTables = useMemo(() => {
    const list = Array.isArray(displayTables) ? displayTables : [];
    const byKey = new Map(list.map((table) => [tableOrderKey(table), table]));
    let ordered;
    if (orderKeys.length === 0) {
      ordered = [...list].sort(
        (a, b) => (a.tableNumber || 0) - (b.tableNumber || 0)
      );
    } else {
      ordered = [];
      orderKeys.forEach((key) => {
        const table = byKey.get(key);
        if (table) ordered.push(table);
      });
      list.forEach((table) => {
        const key = tableOrderKey(table);
        if (!orderKeys.includes(key)) ordered.push(table);
      });
    }
    const stationId = boardTsgId || focusedTsgId;
    return ordered.map((table) => ({
      ...table,
      teId: table.teId || (viewingOther ? null : teId),
      tsgId: table.tsgId || stationId || null,
    }));
  }, [boardTsgId, displayTables, focusedTsgId, orderKeys, teId, viewingOther]);

  const pages = useMemo(
    () => chunkTables(setupTables, tablesPerPage),
    [setupTables, tablesPerPage]
  );
  const pageCount = pages.length;
  const safePageIndex = Math.min(pageIndex, Math.max(0, pageCount - 1));

  const goToPage = useCallback(
    (next) => {
      const clamped = Math.max(0, Math.min(pageCount - 1, next));
      setPageIndex(clamped);
      const scroller = pagesRef.current;
      if (!scroller) return;
      const pageWidth = scroller.clientWidth || 1;
      scroller.scrollTo({ left: clamped * pageWidth, behavior: "smooth" });
    },
    [pageCount]
  );

  useEffect(() => {
    const scroller = pagesRef.current;
    if (!scroller || !open) return undefined;
    const onScroll = () => {
      const pageWidth = scroller.clientWidth || 1;
      const next = Math.round(scroller.scrollLeft / pageWidth);
      setPageIndex((prev) => (prev === next ? prev : next));
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [open, pageCount]);

  useEffect(() => {
    const scroller = pagesRef.current;
    if (!scroller || !open) return;
    const pageWidth = scroller.clientWidth || 1;
    scroller.scrollTo({ left: safePageIndex * pageWidth, behavior: "auto" });
  }, [open, safePageIndex]);

  useEffect(
    () => () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    },
    []
  );

  const triggerFlash = useCallback((keys) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlashSlots(new Set(keys));
    flashTimerRef.current = setTimeout(() => setFlashSlots(new Set()), 1100);
  }, []);

  const isSlotBlocked = useCallback(
    (tableNumber, slot) => tableSetup?.isSlotSwapPending?.(tableNumber, slot),
    [tableSetup]
  );

  const handleSwapSelect = useCallback(
    (table, slotKeyName, player) => {
      if (exerciseLocked || moveBusy) return;
      if (isSlotBlocked(table.tableNumber, slotKeyName)) return;

      const id = seatIdentity(table, slotKeyName);
      const source = swapSource;

      if (!source) {
        if (!player?.playerId) return;
        setSwapSource({
          tableNumber: table.tableNumber,
          tableId: table.tableId,
          teId: table.teId || teId,
          tsgId: table.tsgId || boardTsgId || focusedTsgId,
          slot: slotKeyName,
          id,
          playerId: player.playerId,
          playerName: player?.name || null,
        });
        return;
      }

      if (source.id === id) {
        setSwapSource(null);
        return;
      }

      const sourceTe = source.teId || teId;
      const targetTe = table.teId || (viewingOther ? null : teId);
      const crossTe =
        sourceTe && targetTe && String(sourceTe) !== String(targetTe);
      const crossStation =
        source.tsgId &&
        table.tsgId &&
        String(source.tsgId) !== String(table.tsgId);
      const leavesFocusedTe =
        teId &&
        ((sourceTe && String(sourceTe) !== String(teId)) ||
          (targetTe && String(targetTe) !== String(teId)));

      const exchange = canExchangeSeatedPlayers(
        {
          playerId: source.playerId,
          tableId: source.tableId,
          teId: sourceTe,
          slot: source.slot,
        },
        {
          playerId: player?.playerId,
          tableId: table.tableId,
          teId: targetTe,
          slot: slotKeyName,
        }
      );

      if (crossTe || crossStation || leavesFocusedTe) {
        if (exchange) {
          setMoveBusy(true);
          void (async () => {
            try {
              const result = await tableSetup?.swapAcrossStations?.(
                {
                  playerId: source.playerId,
                  tableId: source.tableId,
                  teId: sourceTe,
                  slot: source.slot,
                },
                {
                  playerId: player.playerId,
                  tableId: table.tableId,
                  teId: targetTe,
                  slot: slotKeyName,
                }
              );
              if (result?.ok) {
                triggerFlash([
                  slotKey(source.tableNumber, source.slot),
                  slotKey(table.tableNumber, slotKeyName),
                ]);
                setSwapSource(null);
              }
            } finally {
              setMoveBusy(false);
            }
          })();
          return;
        }
        if (source.playerId && !player?.playerId && table.tableId && targetTe) {
          setMoveBusy(true);
          void (async () => {
            try {
              const result = await tableSetup?.movePlayersToStation?.(
                [source.playerId],
                table.tsgId || boardTsgId,
                { tableNumber: table.tableNumber, tableId: table.tableId }
              );
              if (result?.ok) setSwapSource(null);
            } finally {
              setMoveBusy(false);
            }
          })();
        }
        return;
      }

      const sourceTable =
        setupTables.find(
          (row) =>
            source.tableId != null &&
            String(row.tableId) === String(source.tableId)
        ) || source;
      tableSetup?.queueSwapPlayers?.(
        sourceTable,
        source.slot,
        table,
        slotKeyName
      );
      triggerFlash([
        slotKey(source.tableNumber, source.slot),
        slotKey(table.tableNumber, slotKeyName),
      ]);
      setSwapSource(null);
    },
    [
      boardTsgId,
      exerciseLocked,
      focusedTsgId,
      isSlotBlocked,
      moveBusy,
      setupTables,
      swapSource,
      tableSetup,
      teId,
      triggerFlash,
      viewingOther,
    ]
  );

  const handleMoveSelectPlayer = useCallback(
    (table, slotKeyName, player) => {
      if (exerciseLocked || moveBusy) return;
      if (!player?.playerId) return;
      const id = String(player.playerId);
      if (moveSource?.playerId === id) {
        setMoveSource(null);
        return;
      }
      setMoveSource({
        playerId: id,
        name: player?.name || t("player") || "Player",
        fromTableNumber: table.tableNumber,
        fromTableId: table.tableId ?? null,
        fromTeId: table.teId || teId,
        fromTsgId: table.tsgId || boardTsgId || focusedTsgId,
        fromSlot: slotKeyName,
      });
    },
    [boardTsgId, exerciseLocked, focusedTsgId, moveBusy, moveSource?.playerId, t, teId]
  );

  const handleMoveSelectTable = useCallback(
    async (table) => {
      if (exerciseLocked || moveBusy || !moveSource?.playerId) return;
      const tn = Number(table?.tableNumber);
      if (!Number.isFinite(tn) || tn <= 0) return;
      const sameTable =
        moveSource.fromTableId != null &&
        table?.tableId != null &&
        String(moveSource.fromTableId) === String(table.tableId);
      if (sameTable) {
        setMoveSource(null);
        return;
      }
      const destTsg = table.tsgId || boardTsgId || focusedTsgId;
      const sourceTsg = moveSource.fromTsgId || focusedTsgId;
      const crossStation =
        (destTsg && sourceTsg && String(destTsg) !== String(sourceTsg)) ||
        (table.teId && teId && String(table.teId) !== String(teId));
      setMoveBusy(true);
      try {
        const result = crossStation
          ? await tableSetup?.movePlayersToStation?.(
              [moveSource.playerId],
              destTsg,
              { tableNumber: tn, tableId: table.tableId ?? null }
            )
          : await tableSetup?.movePlayerForce?.(
              moveSource.playerId,
              tn,
              table.tableId ?? null
            );
        if (result?.ok) {
          triggerFlash([
            slotKey(moveSource.fromTableNumber, moveSource.fromSlot || "player1"),
            slotKey(tn, "player1"),
          ]);
          setMoveSource(null);
        }
      } finally {
        setMoveBusy(false);
      }
    },
    [boardTsgId, exerciseLocked, focusedTsgId, moveBusy, moveSource, tableSetup, teId, triggerFlash]
  );

  const handleSwapRetry = useCallback(
    (opId) => {
      tableSetup?.retrySwap?.(opId);
    },
    [tableSetup]
  );

  const portalRoot = getLiveTrainingPortalRoot();
  if (!open || !portalRoot) return null;

  const tableCapacity = roomCapacity || setupTables.length || 0;
  const pickingSwap = mode === "swap" && Boolean(swapSource);
  const pickingMove = mode === "move" && Boolean(moveSource);
  const statusMessage =
    mode === "move"
      ? pickingMove
        ? t("liveTrainingTableMoveSourceSelected", {
            name: moveSource.name,
          }) || `Selected: ${moveSource.name} — tap a table`
        : t("liveTrainingTableMoveHint") ||
          "Tap a player, then tap a destination table."
      : pickingSwap
        ? t("liveTrainingTableSwapSourceSelected", {
            name: swapSource.playerName || t("player") || "Player",
          }) ||
          `Selected: ${swapSource.playerName} — tap another slot`
        : multiStation
          ? t("liveTrainingTableSwapAcrossHint") ||
            "Tap a player, switch station if needed, then tap another player to swap."
          : t("liveTrainingTableWizardSwapHint") ||
            "Tap a player, then tap another slot to swap.";

  return createPortal(
    <div
      className={`lt-table-wizard__backdrop lt-table-swap-dialog__backdrop${
        elevated ? " lt-table-swap-dialog__backdrop--elevated" : ""
      }`}
      role="presentation"
    >
      <div
        className="lt-table-wizard lt-table-swap-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lt-table-swap-title"
        style={{
          "--lt-brand": brandPrimary,
          "--lt-brand-secondary": brandSecondary,
          "--lt-tables-per-page": String(tablesPerPage),
          "--lt-tables-cols": String(gridColumns),
          "--lt-tables-rows": String(Math.ceil(tablesPerPage / gridColumns)),
        }}
      >
        <header className="lt-table-wizard__header lt-table-swap-dialog__header">
          <div>
            <h2 id="lt-table-swap-title">
              <SwapHorizIcon fontSize="small" aria-hidden />
              {t("liveTrainingTableWizardStepSwap") || "Swap players"}
            </h2>
            <p className="lt-table-wizard__subtitle">
              {usedTableCount}/{tableCapacity} {t("tables") || "tables"}
            </p>
          </div>
          <div className="lt-table-swap-dialog__header-tools">
            <button
              type="button"
              className={`lt-table-swap-dialog__mode-btn${
                mode === "move" ? " is-active" : ""
              }`}
              onClick={() => {
                setMode((prev) => (prev === "move" ? "swap" : "move"));
                setSwapSource(null);
                setMoveSource(null);
              }}
              disabled={exerciseLocked || moveBusy}
              title={t("liveTrainingTableMoveTitle") || "Move player"}
              aria-pressed={mode === "move"}
            >
              <OpenWithIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="lt-table-wizard__close"
              onClick={onClose}
              aria-label={t("close")}
            >
              <CloseIcon />
            </button>
          </div>
        </header>

        {multiStation ? (
          <div
            className="lt-table-swap-dialog__stations"
            role="tablist"
            aria-label={t("liveTrainingTableMoveToStation") || "Station"}
          >
            {stations.map((station) => {
              const id = String(station.tsg_id);
              const selected =
                String(boardTsgId || focusedTsgId || "") === id;
              const label =
                (stationLabelsById && stationLabelsById[id]) ||
                station.name ||
                id;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`lt-table-swap-dialog__station-btn${
                    selected ? " is-selected" : ""
                  }`}
                  disabled={exerciseLocked || moveBusy}
                  onClick={() => {
                    setBoardTsgId(id);
                    setPageIndex(0);
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="lt-table-wizard__body lt-table-swap-dialog__body">
          {(tableSetup?.loading || foreignLoading) && setupTables.length === 0 ? (
            <p className="lt-table-wizard__loading">{t("loading") || "Loading…"}</p>
          ) : setupTables.length === 0 ? (
            <p className="lt-table-wizard__empty-msg">
              {t("noTablesConfigured") || "No tables configured."}
            </p>
          ) : (
            <div className="lt-table-swap-dialog__pager">
              {pageCount > 1 ? (
                <button
                  type="button"
                  className="lt-table-swap-dialog__edge lt-table-swap-dialog__edge--prev"
                  onClick={() => goToPage(safePageIndex - 1)}
                  disabled={safePageIndex <= 0}
                  aria-label={t("previous", "Previous")}
                >
                  <ChevronLeftIcon />
                </button>
              ) : null}
              <div
                className="lt-table-swap-dialog__pages"
                ref={pagesRef}
                role="region"
                aria-label={t("tables") || "Tables"}
              >
                {pages.map((pageItems, pageIdx) => {
                  const slots = padPageSlots(pageItems, tablesPerPage);
                  return (
                    <div
                      key={`swap-page-${pageIdx}`}
                      className="lt-table-swap-dialog__page"
                    >
                      <div className="lt-table-swap-dialog__tables-grid">
                        {slots.map((table, slotIdx) => {
                          if (!table) {
                            return (
                              <div
                                key={`empty-${pageIdx}-${slotIdx}`}
                                className="lt-coach-table lt-coach-table--slot-empty lt-table-swap-dialog__card"
                                aria-hidden
                              />
                            );
                          }

                          const p1Key = slotKey(table.tableNumber, "player1");
                          const p2Key = slotKey(table.tableNumber, "player2");
                          const p1Blocked = isSlotBlocked(
                            table.tableNumber,
                            "player1"
                          );
                          const p2Blocked = isSlotBlocked(
                            table.tableNumber,
                            "player2"
                          );

                          if (mode === "move") {
                            const isSourceTable =
                              moveSource?.fromTableId != null &&
                              table.tableId != null &&
                              String(moveSource.fromTableId) ===
                                String(table.tableId);
                            const selectedSlot =
                              isSourceTable && moveSource?.fromSlot
                                ? moveSource.fromSlot
                                : null;
                            const isTargetTable =
                              pickingMove && !isSourceTable;
                            return (
                              <CoachTablePreviewCard
                                key={tableOrderKey(table) || table.tableNumber}
                                tableNumber={table.tableNumber}
                                player1={table.player1}
                                player2={table.player2}
                                player3={table.player3}
                                extras={table.extras}
                                t={t}
                                swapMode={!isTargetTable}
                                hideSwapBadges
                                disabled={exerciseLocked || moveBusy}
                                selectedSlot={selectedSlot}
                                player1Flash={flashSlots.has(p1Key)}
                                player2Flash={flashSlots.has(p2Key)}
                                onSelectPlayer1={() =>
                                  handleMoveSelectPlayer(
                                    table,
                                    "player1",
                                    table.player1
                                  )
                                }
                                onSelectPlayer2={() =>
                                  handleMoveSelectPlayer(
                                    table,
                                    "player2",
                                    table.player2
                                  )
                                }
                                onSelectPlayer3={() =>
                                  handleMoveSelectPlayer(
                                    table,
                                    "player3",
                                    table.player3
                                  )
                                }
                                onSelectExtra={(player, index) =>
                                  handleMoveSelectPlayer(
                                    table,
                                    `extra:${index}`,
                                    player
                                  )
                                }
                                onTableClick={
                                  isTargetTable
                                    ? () => handleMoveSelectTable(table)
                                    : undefined
                                }
                                className={[
                                  "lt-table-wizard__card",
                                  "lt-table-swap-dialog__card",
                                  isTargetTable
                                    ? "lt-table-swap-dialog__card--target-table"
                                    : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                              />
                            );
                          }

                          const p3Key = slotKey(table.tableNumber, "player3");
                          const p3Blocked = isSlotBlocked(
                            table.tableNumber,
                            "player3"
                          );
                          const p1IsSource =
                            swapSource?.id === seatIdentity(table, "player1");
                          const p2IsSource =
                            swapSource?.id === seatIdentity(table, "player2");
                          const p3IsSource =
                            swapSource?.id === seatIdentity(table, "player3");
                          const p1IsTarget =
                            pickingSwap && !p1IsSource && !p1Blocked;
                          const p2IsTarget =
                            pickingSwap && !p2IsSource && !p2Blocked;
                          const p3IsTarget =
                            pickingSwap && !p3IsSource && !p3Blocked;
                          const selectedSlots = [
                            p1IsSource ? "player1" : null,
                            p2IsSource ? "player2" : null,
                            p3IsSource ? "player3" : null,
                          ].filter(Boolean);
                          const targetSlots = [
                            p1IsTarget ? "player1" : null,
                            p2IsTarget ? "player2" : null,
                            p3IsTarget ? "player3" : null,
                          ].filter(Boolean);
                          return (
                            <CoachTablePreviewCard
                              key={tableOrderKey(table) || table.tableNumber}
                              tableNumber={table.tableNumber}
                              player1={table.player1}
                              player2={table.player2}
                              player3={table.player3}
                              extras={table.extras}
                              t={t}
                              swapMode
                              allowEmptySelect={pickingSwap}
                              hideSwapBadges
                              selectedSlot={
                                selectedSlots.length ? selectedSlots : null
                              }
                              targetSlots={
                                targetSlots.length ? targetSlots : null
                              }
                              player1SwapTarget={p1IsTarget}
                              player2SwapTarget={p2IsTarget}
                              player3SwapTarget={p3IsTarget}
                              player1Dimmed={
                                pickingSwap && !p1IsSource && !p1IsTarget
                              }
                              player2Dimmed={
                                pickingSwap && !p2IsSource && !p2IsTarget
                              }
                              player3Dimmed={
                                pickingSwap && !p3IsSource && !p3IsTarget
                              }
                              player1Flash={flashSlots.has(p1Key)}
                              player2Flash={flashSlots.has(p2Key)}
                              player1SwapState={tableSetup?.getSlotSwapState?.(
                                table.tableNumber,
                                "player1"
                              )}
                              player2SwapState={tableSetup?.getSlotSwapState?.(
                                table.tableNumber,
                                "player2"
                              )}
                              onSwapRetry={handleSwapRetry}
                              onSelectPlayer1={() =>
                                handleSwapSelect(
                                  table,
                                  "player1",
                                  table.player1
                                )
                              }
                              onSelectPlayer2={() =>
                                handleSwapSelect(
                                  table,
                                  "player2",
                                  table.player2
                                )
                              }
                              onSelectPlayer3={() =>
                                handleSwapSelect(
                                  table,
                                  "player3",
                                  table.player3
                                )
                              }
                              onSelectExtra={(player, index) => {
                                setMode("move");
                                setSwapSource(null);
                                handleMoveSelectPlayer(
                                  table,
                                  `extra:${index}`,
                                  player
                                );
                              }}
                              className="lt-table-wizard__card lt-table-swap-dialog__card"
                              />
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {pageCount > 1 ? (
                <button
                  type="button"
                  className="lt-table-swap-dialog__edge lt-table-swap-dialog__edge--next"
                  onClick={() => goToPage(safePageIndex + 1)}
                  disabled={safePageIndex >= pageCount - 1}
                  aria-label={t("next", "Next")}
                >
                  <ChevronRightIcon />
                </button>
              ) : null}
            </div>
          )}
        </div>

        <div
          className={`lt-table-swap-dialog__status${
            pickingSwap || pickingMove
              ? " lt-table-swap-dialog__status--picking"
              : ""
          }`}
          role="status"
        >
          <span className="lt-table-swap-dialog__status-text">
            {mode === "move" ? (
              <OpenWithIcon fontSize="small" aria-hidden />
            ) : pickingSwap ? (
              <SwapHorizIcon fontSize="small" aria-hidden />
            ) : null}
            {statusMessage}
          </span>
          {pickingSwap || pickingMove ? (
            <button
              type="button"
              className="lt-table-swap-dialog__source-cancel"
              onClick={() => {
                setSwapSource(null);
                setMoveSource(null);
              }}
              disabled={moveBusy}
            >
              <CloseIcon fontSize="small" aria-hidden />
              {t("cancel")}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    portalRoot
  );
}

export default LiveTrainingTableSwapDialog;
